/* Keksmeister: Widerrufsfunktion nach § 356a BGB (sections/km-widerruf.liquid).
 *
 * Schritt 1 Formular → Schritt 2 Zusammenfassung mit „Widerruf bestätigen“ → Schritt 3 Eingang.
 * Übermittlung als Ereignis „Widerruf eingegangen“ an die Klaviyo Client-API (öffentlicher Schlüssel,
 * kein Geheimnis). Der Klaviyo-Flow schickt daraus die Eingangsbestätigung an die Kundin und eine
 * Benachrichtigung an den Shop (marketing/klaviyo/widerruf-flow.md).
 *
 * Bewusst ohne klaviyo.js, ohne Cookies, ohne localStorage und ohne Consent-Abfrage: Die Erklärung dient der
 * Vertragsabwicklung (Art. 6 Abs. 1 lit. b und c DSGVO, § 356a BGB) und nicht der Analyse oder Werbung;
 * es wird nichts auf dem Gerät gespeichert oder ausgelesen (§ 25 TDDDG greift nicht).
 *
 * Fehler oder kein Schlüssel: Die Angaben bleiben erhalten, die Kundin bekommt einen vorbefüllten mailto-Link
 * an die Shop-Adresse und den Text zum Kopieren.
 */
(() => {
  const API = 'https://a.klaviyo.com/client/events';
  const REVISION = '2026-10-15'; // aktuelle Revision laut developers.klaviyo.com (Create Client Event), Stand 30.10.2026
  const TIMEOUT_MS = 15000;

  const berlin = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const readable = (d) => `${berlin.format(d)} Uhr`;

  const uid = () => {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  };

  /** "#1104", "1104", " # 1104 " → "#1104"; null wenn ungültig. */
  const normOrder = (v) => {
    const t = String(v || '').replace(/\s+/g, '').replace(/^#+/, '').replace(/^(nr\.?|bestellung)/i, '');
    return /^[A-Za-z0-9-]{1,20}$/.test(t) ? `#${t.toUpperCase()}` : null;
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  class KmWiderruf extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.key = (this.dataset.key || '').trim();
      this.mail = this.dataset.mail || 'support@keksmeister.de';
      this.metric = this.dataset.metric || 'Widerruf eingegangen';
      this.form = this.querySelector('[data-km-wr-form]');
      this.form.noValidate = true; // eigene, vorgelesene Fehlermeldungen statt Browser-Blasen
      this.sending = false;
      this.sent = false;
      this.data = null;

      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.review(); });
      this.querySelectorAll('[data-km-wr-scope]').forEach((r) => r.addEventListener('change', () => this.syncScope()));
      this.querySelector('[data-km-wr-back]').addEventListener('click', () => this.show('1'));
      this.querySelector('[data-km-wr-confirm]').addEventListener('click', () => this.submit());
      this.querySelector('[data-km-wr-retry]').addEventListener('click', () => this.show('2'));
      this.syncScope();
    }

    $(sel) { return this.querySelector(sel); }

    field(name) { return this.form.elements[name]; }

    syncScope() {
      const partial = this.scope() === 'nur einzelne Artikel';
      const items = this.field('Artikel');
      items.required = partial;
      this.$('[data-km-wr-items-star]').hidden = !partial;
    }

    scope() {
      const r = this.form.querySelector('[data-km-wr-scope]:checked');
      return r ? r.value : 'ganze Bestellung';
    }

    show(step) {
      this.querySelectorAll('[data-km-wr-step]').forEach((el) => { el.hidden = el.dataset.kmWrStep !== step; });
      const panel = this.querySelector(`[data-km-wr-step="${step}"]`);
      const h = panel && panel.querySelector('.km-wr__title');
      if (h) {
        h.focus({ preventScroll: true });
        this.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    }

    setError(key, input, msg) {
      const p = this.querySelector(`[data-km-wr-err="${key}"]`);
      if (p) { p.textContent = msg || ''; p.hidden = !msg; }
      if (input) {
        if (msg) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      }
    }

    /** Schritt 1 prüfen → Schritt 2 */
    review() {
      const name = this.field('Name').value.trim();
      const email = this.field('E-Mail').value.trim();
      const orderRaw = this.field('Bestellnummer').value;
      const order = normOrder(orderRaw);
      const scope = this.scope();
      const items = this.field('Artikel').value.trim();
      const msg = this.field('Nachricht').value.trim();

      const errors = [];
      const check = (key, input, ok, text) => {
        this.setError(key, input, ok ? '' : text);
        if (!ok) errors.push({ input, text });
      };
      check('name', this.field('Name'), name.length >= 2, 'Bitte gib deinen Vor- und Nachnamen ein.');
      check('email', this.field('E-Mail'), EMAIL_RE.test(email), email ? 'Diese E-Mail-Adresse sieht nicht vollständig aus, z. B. name@beispiel.de.' : 'Bitte gib deine E-Mail-Adresse ein, damit wir dir den Eingang bestätigen können.');
      check('order', this.field('Bestellnummer'), !!order, orderRaw.trim() ? 'Die Bestellnummer besteht aus Ziffern, z. B. #1104.' : 'Bitte gib deine Bestellnummer ein, z. B. #1104.');
      check('items', this.field('Artikel'), scope !== 'nur einzelne Artikel' || items.length > 0, 'Bitte schreib, welche Artikel du widerrufst.');

      const box = this.$('[data-km-wr-errors]');
      if (errors.length) {
        box.innerHTML = '';
        const head = document.createElement('p');
        head.style.margin = '0';
        head.textContent = errors.length === 1 ? 'Eine Angabe fehlt noch:' : `${errors.length} Angaben fehlen noch:`;
        const ul = document.createElement('ul');
        errors.forEach((er) => { const li = document.createElement('li'); li.textContent = er.text; ul.append(li); });
        box.append(head, ul);
        box.hidden = false;
        errors[0].input.focus();
        return;
      }
      box.hidden = true;
      box.innerHTML = '';

      // Eine ID je Erklärung: „Noch einmal versuchen“ nach einem verlorenen 202 legt in Klaviyo kein zweites Ereignis an
      const same = this.data && this.data.name === name && this.data.email === email && this.data.order === order
        && this.data.scope === scope && this.data.msg === msg && this.data.items === (scope === 'nur einzelne Artikel' ? items : '');
      this.data = { name, email, order, scope, items: scope === 'nur einzelne Artikel' ? items : '', msg, id: same ? this.data.id : uid() };
      this.fillSummary(this.data, null);
      this.$('[data-km-wr-status]').textContent = '';
      this.show('2');
    }

    rows(d, at) {
      const r = [
        ['Name', d.name],
        ['E-Mail', d.email],
        ['Bestellnummer', d.order],
        ['Umfang', d.scope === 'ganze Bestellung' ? 'Widerruf der ganzen Bestellung' : 'Widerruf einzelner Artikel'],
      ];
      if (d.items) r.push(['Artikel', d.items]);
      if (d.msg) r.push(['Nachricht', d.msg]);
      if (at) r.push(['Eingegangen', readable(at)]);
      return r;
    }

    fillSummary(d, at) {
      this.querySelectorAll('[data-km-wr-summary]').forEach((dl) => {
        dl.innerHTML = '';
        this.rows(d, at).forEach(([k, v]) => {
          const dt = document.createElement('dt');
          const dd = document.createElement('dd');
          dt.textContent = k;
          dd.textContent = v;
          dl.append(dt, dd);
        });
      });
    }

    plainText(d) {
      const lines = [
        `An Insta Solutions GmbH, Keksmeister, Otto-Hahn-Straße 21, 50997 Köln, ${this.mail}`,
        '',
        d.scope === 'ganze Bestellung'
          ? `Hiermit widerrufe ich den von mir abgeschlossenen Vertrag über die Bestellung ${d.order}.`
          : `Hiermit widerrufe ich den von mir abgeschlossenen Vertrag über folgende Artikel der Bestellung ${d.order}:\n${d.items}`,
        '',
        `Name: ${d.name}`,
        `E-Mail: ${d.email}`,
        `Bestellnummer: ${d.order}`,
        `Umfang: ${d.scope}`,
      ];
      if (d.items) lines.push(`Artikel: ${d.items}`);
      if (d.msg) lines.push(`Nachricht: ${d.msg}`);
      lines.push(`Datum: ${readable(new Date())}`);
      return lines.join('\n');
    }

    fail(text) {
      const d = this.data;
      const body = this.plainText(d);
      const href = `mailto:${this.mail}?subject=${encodeURIComponent(`Widerruf Bestellung ${d.order}`)}&body=${encodeURIComponent(body)}`;
      this.$('[data-km-wr-mail]').href = href;
      this.$('[data-km-wr-copy]').value = body;
      this.$('[data-km-wr-error-text]').textContent = text;
      // Ohne Schlüssel hilft „Noch einmal versuchen“ nicht
      this.$('[data-km-wr-retry]').hidden = !this.key;
      this.show('error');
    }

    async submit() {
      if (this.sending || this.sent || !this.data) return; // Doppelklick-Schutz
      const btn = this.$('[data-km-wr-confirm]');
      const back = this.$('[data-km-wr-back]');
      const status = this.$('[data-km-wr-status]');

      if (!this.key) {
        this.fail('Die Online-Übermittlung ist gerade nicht eingerichtet. Deine Angaben sind noch da.');
        return;
      }

      this.sending = true;
      btn.setAttribute('aria-disabled', 'true');
      back.disabled = true;
      this.setAttribute('aria-busy', 'true');
      status.textContent = 'Dein Widerruf wird übermittelt …';

      const d = this.data;
      const at = new Date();
      const { id } = d;
      const payload = {
        data: {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: this.metric } } },
            // Nur die E-Mail: Der Name aus einem ungeprüften Formular darf vorhandene Klaviyo-Profile nicht überschreiben (30.10.2026).
            profile: { data: { type: 'profile', attributes: { email: d.email } } },
            properties: {
              Name: d.name,
              'E-Mail': d.email,
              Bestellnummer: d.order,
              Umfang: d.scope,
              Artikel: d.items,
              Nachricht: d.msg,
              Eingang: readable(at),
              EingangISO: at.toISOString(),
              Seite: location.href.split('#')[0],
              WiderrufID: id,
            },
            time: at.toISOString(),
            unique_id: id,
          },
        },
      };

      const ctrl = window.AbortController ? new AbortController() : null;
      const timer = ctrl ? setTimeout(() => ctrl.abort(), TIMEOUT_MS) : 0;
      let ok = false;
      try {
        const res = await fetch(`${API}?company_id=${encodeURIComponent(this.key)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: REVISION },
          body: JSON.stringify(payload),
          credentials: 'omit',
          mode: 'cors',
          keepalive: true,
          signal: ctrl ? ctrl.signal : undefined,
        });
        ok = res.status === 202 || res.ok;
      } catch (_) {
        ok = false;
      } finally {
        clearTimeout(timer);
        this.sending = false;
        btn.removeAttribute('aria-disabled');
        back.disabled = false;
        this.removeAttribute('aria-busy');
        status.textContent = '';
      }

      if (!ok) {
        this.fail('Dein Widerruf konnte gerade nicht übermittelt werden, zum Beispiel wegen der Internetverbindung. Deine Angaben sind noch da.');
        return;
      }
      this.sent = true;
      this.fillSummary(d, at);
      this.$('[data-km-wr-time]').textContent = readable(at);
      this.$('[data-km-wr-mailto]').textContent = d.email;
      this.show('3');
    }
  }

  if (!customElements.get('km-widerruf')) customElements.define('km-widerruf', KmWiderruf);
})();
